import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Transaction schema — uses passthrough because the column set is dynamic
 * (defined by pipeline workflow config, not the contract). Only `id` is guaranteed.
 */
const TransactionSchema = z
  .object({
    id: z.string(),
  })
  .passthrough();

const PaginationSchema = z.object({
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});

/** Matches snake_case column names (lowercase start, then lowercase/digits/_). */
const ColumnNameSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]*$/, "Invalid column name");

// Transaction lifecycle status. "posted" is NOT a status — it's an orthogonal
// fact tracked by the `posted_at` timestamp (a posted row stays `staged`). Use
// PostingStatusFilterSchema for GL-posting filters that distinguish staged vs
// posted. "deleted" is retained for legacy rows (normalized to no_claim on read).
const SdE844dStatusSchema = z.enum([
  "pending",
  "staged",
  "staged_internal",
  "processing",
  "sent",
  "resubmission_pending",
  "no_claim",
  "deleted",
]);

/**
 * Logical status filter for GL-posting flows (download + post). "posted" here
 * means the derived flag (staged AND posted_at set); "staged" means un-posted
 * staged. The backend translates these via buildSdStatusWhere.
 */
const PostingStatusFilterSchema = z.enum(["staged", "posted", "sent"]);

// New consolidated List input — superset of all existing list schemas
export const ListSdTransactionsInputSchema = z.object({
  statuses: z.array(SdE844dStatusSchema).min(1),
  limit: z.number().int().positive().max(1000).optional().default(100),
  offset: z.number().int().nonnegative().optional().default(0),
  columnFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  textFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  dateFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  numericFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  hasComments: z.boolean().optional(),
  search: z.string().optional(),
  searchColumns: z.array(ColumnNameSchema).optional(),
  sortBy: z.string().optional(),
  sortDirection: z.enum(["asc", "desc"]).optional().default("desc"),
  transactionIds: z.array(z.string().uuid()).optional(),
});

export const ListSdTransactionsResponseSchema = z.object({
  transactions: z.array(TransactionSchema),
  pagination: PaginationSchema,
});

export const ListSdTransactionIdsInputSchema = z.object({
  statuses: z.array(SdE844dStatusSchema).min(1),
  columnFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  textFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  dateFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  numericFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  hasComments: z.boolean().optional(),
  search: z.string().optional(),
  searchColumns: z.array(ColumnNameSchema).optional(),
});

export const ListSdTransactionIdsResponseSchema = z.object({
  transactionIds: z.array(z.string()),
});

/**
 * Mark Transactions As No-Claim
 */
export const MarkAsNoClaimInputSchema = z.object({
  transactionIds: z.array(z.string()).min(1),
});

export const MarkAsNoClaimResponseSchema = z.object({
  markedCount: z.number().int().nonnegative(),
});

/**
 * Unmark Transactions From No-Claim
 */
export const UnmarkAsNoClaimInputSchema = z.object({
  transactionIds: z.array(z.string()).min(1),
});

export const UnmarkAsNoClaimResponseSchema = z.object({
  unmarkedCount: z.number().int().nonnegative(),
});

/**
 * Stage Transactions
 */
export const StageTransactionsInputSchema = z.object({
  transactionIds: z.array(z.string()).min(1),
});

export const StageTransactionsResponseSchema = z.object({
  stagedCount: z.number().int().nonnegative(),
});

/**
 * Unstage Transactions
 */
export const UnstageTransactionsInputSchema = z.union([
  z.object({ transactionIds: z.array(z.string()).min(1) }),
  z.object({ all: z.literal(true) }),
]);

export const UnstageTransactionsResponseSchema = z.object({
  unstagedCount: z.number().int().nonnegative(),
});

/**
 * Un-post Transactions — reverse a GL posting so the rows can be edited again.
 * Clears posted_at (back to plain `staged`); does not unstage. Admin/posting
 * action.
 */
export const UnpostTransactionsInputSchema = z.object({
  transactionIds: z.array(z.string()).min(1),
});

export const UnpostTransactionsResponseSchema = z.object({
  unpostedCount: z.number().int().nonnegative(),
});

/**
 * Update Staged Transaction
 */
export const UpdateStagedTransactionInputSchema = z.object({
  transactionId: z.string(),
  updates: z.record(
    z.string().regex(/^[a-z][a-z0-9_]*$/, "Invalid column name"),
    z.unknown(),
  ),
});

export const AddCommentInputSchema = z.object({
  transactionId: z.string(),
  text: z.string().min(1),
});

export const AddCommentResponseSchema = z.object({
  success: z.boolean(),
});

export const EditCommentInputSchema = z.object({
  activityId: z.string(),
  text: z.string().min(1),
});

export const EditCommentResponseSchema = z.object({
  success: z.boolean(),
});

export const DeleteCommentInputSchema = z.object({
  activityId: z.string(),
});

export const DeleteCommentResponseSchema = z.object({
  success: z.boolean(),
});

export const UpdateStagedTransactionResponseSchema = z.object({
  success: z.boolean(),
  transaction: TransactionSchema,
});

/**
 * Bulk Update Transactions — apply one field change and/or a comment to many
 * rows at once. `updates` may contain zero or one key; `comment` is optional.
 * At least one of them must be provided.
 */
export const BulkUpdateTransactionsInputSchema = z
  .object({
    transactionIds: z.array(z.string()).min(1),
    updates: z
      .record(
        z.string().regex(/^[a-z][a-z0-9_]*$/, "Invalid column name"),
        z.unknown(),
      )
      .refine((v) => Object.keys(v).length <= 1, {
        message: "At most one field may be updated at a time",
      }),
    comment: z.string().min(1).optional(),
  })
  .refine((v) => Object.keys(v.updates).length > 0 || v.comment, {
    message: "At least one of updates or comment must be provided",
  });

export const BulkUpdateTransactionsResponseSchema = z.object({
  updatedCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative(),
  recalculatedCount: z.number().int().nonnegative(),
  commentedCount: z.number().int().nonnegative(),
});

/**
 * Stage Internal Transactions
 */
export const StageInternalTransactionsInputSchema = z.object({
  transactionIds: z.array(z.string()).min(1),
});

export const StageInternalTransactionsResponseSchema = z.object({
  stagedCount: z.number().int().nonnegative(),
});

/**
 * Unstage Internal Transactions
 */
export const UnstageInternalTransactionsInputSchema = z.union([
  z.strictObject({ transactionIds: z.array(z.string()).min(1) }),
  z.strictObject({ all: z.literal(true) }),
]);

export const UnstageInternalTransactionsResponseSchema = z.object({
  unstagedCount: z.number().int().nonnegative(),
});

/**
 * Get Staged Stats
 */
export const GetStagedStatsInputSchema = z.object({});

export const GetStagedStatsResponseSchema = z.object({
  transactionCount: z.number().int().nonnegative(),
  totalDebitAmount: z.number(),
  positiveDebitAmount: z.number(),
  negativeDebitAmount: z.number(),
  vendorCount: z.number().int().nonnegative(),
});

/**
 * Get Staged Supplier Breakdown (for Batch & Send confirmation dialog)
 */
export const GetStagedSupplierBreakdownInputSchema = z.object({
  // When provided, restrict the breakdown to these specific IDs. Used by
  // the Batch and Send dialog so the breakdown matches the actual
  // selection even when filters hide some selected rows.
  transactionIds: z.array(z.string().uuid()).optional(),
  columnFilters: z.record(z.string(), z.array(z.string())).optional(),
  textFilters: z.record(z.string(), z.array(z.string())).optional(),
  dateFilters: z.record(z.string(), z.array(z.string())).optional(),
  numericFilters: z.record(z.string(), z.array(z.string())).optional(),
  hasComments: z.boolean().optional(),
});

export const GetStagedSupplierBreakdownResponseSchema = z.object({
  breakdown: z.array(
    z.object({
      supplierName: z.string(),
      deliveryMethod: z.enum([
        "edi",
        "email",
        "unmatched",
        "missing_duns",
        "missing_email",
      ]),
      count: z.number().int().nonnegative(),
      claimAmount: z.number(),
    }),
  ),
  totalClaimAmount: z.number(),
});

/**
 * Get Selected Claim Amount — sums claim_amount for the given staged
 * transaction IDs. Used by the staged page header to show the running
 * total of the user's current selection.
 */
export const GetSelectedClaimAmountInputSchema = z.object({
  transactionIds: z.array(z.string().uuid()).min(1),
});

export const GetSelectedClaimAmountResponseSchema = z.object({
  claimAmount: z.number(),
});

/**
 * Download Staged Excel (read-only preview of email-path Excel)
 */
export const DownloadStagedExcelInputSchema = z.object({
  transactionIds: z.array(z.string()).min(1),
});

export const DownloadStagedExcelResponseSchema = z.object({
  /** base64-encoded .xlsx bytes */
  excel: z.string(),
  /** Suggested filename */
  filename: z.string(),
});

/**
 * Get Filter Options
 */
export const GetFilterOptionsInputSchema = z.object({});

export const GetFilterOptionsResponseSchema = z.object({
  division: z.array(z.string()),
  company: z.array(z.string()),
  selling_location: z.array(z.string()),
  trading_partner: z.array(z.string()),
  ship_type: z.array(z.string()),
  rebate_type: z.array(z.string()),
  suppliers: z.array(z.object({ id: z.string(), name: z.string() })),
  /** Trading partner code → supplier display name (for filter dropdown labels) */
  trading_partner_names: z.record(z.string(), z.string()),
  /** Selling location code → "{code} {Title Case description}" (for branch dropdown labels) */
  selling_location_labels: z.record(z.string(), z.string()),
  /** Company code → "{Name} ({code})" (for company dropdown labels) */
  company_labels: z.record(z.string(), z.string()),
  /** Division code → "{Name} ({code})" (for division dropdown labels) */
  division_labels: z.record(z.string(), z.string()),
});

/**
 * Get User Activity Log (user actions only, no system events)
 */
export const GetActivityLogInputSchema = z.object({
  activityTypes: z.array(z.string()).optional(),
  userIds: z.array(z.string()).optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  limit: z.number().int().positive().max(200).optional().default(50),
  offset: z.number().int().nonnegative().optional().default(0),
});

export const GetActivityLogResponseSchema = z.object({
  activities: z.array(
    z.object({
      id: z.string(),
      activityType: z.string(),
      description: z.string(),
      eventAt: z.string(),
      userId: z.string().nullable(),
      userName: z.string().nullable(),
      transactionId: z.string(),
      invoiceNo: z.string().nullable(),
      division: z.string().nullable(),
      tradingPartner: z.string().nullable(),
      supplierName: z.string().nullable(),
      metadata: z.record(z.string(), z.unknown()).nullable(),
    }),
  ),
  pagination: PaginationSchema,
  filterOptions: z.object({
    activityTypes: z.array(z.string()),
    users: z.array(z.object({ id: z.string(), name: z.string() })),
  }),
  userSummary: z.array(
    z.object({
      userId: z.string(),
      userName: z.string(),
      total: z.number().int().nonnegative(),
      activityCounts: z.record(z.string(), z.number().int().nonnegative()),
    }),
  ),
});

/**
 * Search All Transactions (across all statuses)
 */
export const SearchAllTransactionsInputSchema = z.object({
  divisions: z.array(z.string()).optional(),
  sellingLocations: z.array(z.string()).optional(),
  /** Supplier company UUIDs */
  companyIds: z.array(z.string()).optional(),
  invoiceNo: z.string().optional(),
  orderLineNo: z.string().optional(),
  /** Free-text search against customer name and sold-to/ship-to numbers.
   * Use `*` as a wildcard (e.g. "Acme*", "*Corp", "*Acme*"). With no `*`,
   * matches as a substring. */
  customer: z.string().optional(),
  /** Free-text search across our item #, vendor item #, vendor catalog #, UPC */
  productCode: z.string().optional(),
  /** Free-text substring search against the supplier contract no (SPA quote #) */
  supplierContractNo: z.string().optional(),
  limit: z.number().int().positive().max(200).optional().default(50),
  offset: z.number().int().nonnegative().optional().default(0),
});

export const SearchAllTransactionsResponseSchema = z.object({
  transactions: z.array(
    z.object({
      id: z.string(),
      status: z.string(),
      division: z.string().nullable(),
      sellingLocation: z.string().nullable(),
      invoiceNo: z.string().nullable(),
      orderLineNo: z.string().nullable(),
      tradingPartner: z.string().nullable(),
      supplierName: z.string().nullable(),
      claimAmount: z.number().nullable(),
      supplierContractNo: z.string().nullable(),
      rebateType: z.string().nullable(),
      shipType: z.string().nullable(),
      prefixItemNumber: z.string().nullable(),
      submissionId: z.string().nullable(),
      deliveryMethod: z.string().nullable(),
      // 849 response info for a sent transaction (matched by invoice + order
      // line). Null when the transaction isn't sent or has no matched 849 line.
      responseId: z.string().nullable(),
      responseStatus: z.string().nullable(),
      approvedAmount: z.number().nullable(),
    }),
  ),
  pagination: PaginationSchema,
});

/**
 * Get Classification Stats — daily/weekly counts of auto-classified transactions
 */
export const GetClassificationStatsInputSchema = z
  .object({
    startDate: z.string().date(),
    endDate: z.string().date(),
    granularity: z.enum(["day", "week"]).optional().default("day"),
  })
  .refine(({ startDate, endDate }) => startDate <= endDate, {
    message: "startDate must not be after endDate",
    path: ["endDate"],
  });

export const GetClassificationStatsResponseSchema = z.object({
  daily: z.array(
    z.object({
      date: z.string(),
      staged: z.number().int().nonnegative(),
      staged_internal: z.number().int().nonnegative(),
      no_claim: z.number().int().nonnegative(),
      total: z.number().int().nonnegative(),
    }),
  ),
  totals: z.object({
    staged: z.number().int().nonnegative(),
    staged_internal: z.number().int().nonnegative(),
    no_claim: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
  }),
});

/**
 * Post Now — manually post all staged transactions (admin action)
 */
export const PostNowInputSchema = z.object({});

export const PostNowResponseSchema = z.object({
  postedCount: z.number().int().nonnegative(),
  /** GL posting workbooks (one per division) */
  postingFiles: z
    .array(
      z.object({
        excel: z.string(),
        filename: z.string(),
        divisionKey: z.string(),
      }),
    )
    .optional(),
  postingWarnings: z
    .array(z.object({ divisionKey: z.string(), message: z.string() }))
    .optional(),
});

/**
 * Download Posting Excel — GL posting workbook for staged/posted transactions
 */
export const DownloadPostingExcelInputSchema = z.object({
  /** Transaction IDs to include. If empty, uses filters to find matching transactions. */
  transactionIds: z.array(z.string()).optional(),
  /** Status filter (defaults to ["staged", "posted"]) */
  statuses: z.array(PostingStatusFilterSchema).optional(),
  /** Exact-match column filters (e.g. division, trading_partner) */
  columnFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  /** Substring (ILIKE) text filters */
  textFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  /** Date range filters */
  dateFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  /** Numeric operator filters (eq:, gte:, lte:) */
  numericFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  /** Filter to only transactions with comments */
  hasComments: z.boolean().optional(),
  /** Free-text search */
  search: z.string().optional(),
  /** Columns to search across */
  searchColumns: z.array(ColumnNameSchema).optional(),
});

export const DownloadPostingExcelResponseSchema = z.object({
  files: z.array(
    z.object({
      excel: z.string(),
      filename: z.string(),
      divisionKey: z.string(),
    }),
  ),
  warnings: z.array(
    z.object({
      divisionKey: z.string(),
      message: z.string(),
    }),
  ),
});

/**
 * Get Status Counts — current transaction counts by status
 */
export const GetStatusCountsInputSchema = z.object({});

export const GetStatusCountsResponseSchema = z.object({
  pending: z.number().int().nonnegative(),
  staged: z.number().int().nonnegative(),
  staged_internal: z.number().int().nonnegative(),
  posted: z.number().int().nonnegative(),
  no_claim: z.number().int().nonnegative(),
});

/**
 * Get Classification Breakdown — daily system/user classification counts + all-time totals
 */
export const GetClassificationBreakdownInputSchema = z
  .object({
    startDate: z.string().date(),
    endDate: z.string().date(),
  })
  .refine(({ startDate, endDate }) => startDate <= endDate, {
    message: "startDate must not be after endDate",
    path: ["endDate"],
  });

export const GetClassificationBreakdownResponseSchema = z.object({
  daily: z.array(
    z.object({
      date: z.string(),
      system: z.number().int().nonnegative(),
      user: z.number().int().nonnegative(),
    }),
  ),
  totals: z.object({
    total: z.number().int().nonnegative(),
    classifiedBySystem: z.number().int().nonnegative(),
    classifiedByUser: z.number().int().nonnegative(),
  }),
});

/**
 * Get User Activity Stats — daily counts of all user-triggered actions
 */
export const GetUserActivityStatsInputSchema = z
  .object({
    startDate: z.string().date(),
    endDate: z.string().date(),
    groupByUser: z.boolean().optional().default(false),
  })
  .refine(({ startDate, endDate }) => startDate <= endDate, {
    message: "startDate must not be after endDate",
    path: ["endDate"],
  });

export const GetUserActivityStatsResponseSchema = z.object({
  daily: z.array(
    z.object({
      date: z.string(),
      total: z.number().int().nonnegative(),
    }),
  ),
  dailyByUser: z
    .array(
      z.object({
        date: z.string(),
        userName: z.string(),
        total: z.number().int().nonnegative(),
      }),
    )
    .optional(),
  users: z.array(z.string()).optional(),
});

/**
 * Get Supplier Acceptance Rates — 849 response acceptance breakdown per EDI supplier
 */
export const GetSupplierAcceptanceRatesInputSchema = z.object({});

export const GetSupplierAcceptanceRatesResponseSchema = z.object({
  suppliers: z.array(
    z.object({
      supplierId: z.string().nullable(),
      supplierName: z.string(),
      accepted: z.number().int().nonnegative(),
      partial: z.number().int().nonnegative(),
      rejected: z.number().int().nonnegative(),
      total: z.number().int().nonnegative(),
      sentLineCount: z.number().int().nonnegative(),
    }),
  ),
  totals: z.object({
    accepted: z.number().int().nonnegative(),
    partial: z.number().int().nonnegative(),
    rejected: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    sentLineCount: z.number().int().nonnegative(),
  }),
});

/**
 * Contracts
 */
export const e844sdTransactionsContracts = {
  list: oc
    .input(ListSdTransactionsInputSchema)
    .output(ListSdTransactionsResponseSchema)
    .route({ method: "GET" }),
  listIds: oc
    .input(ListSdTransactionIdsInputSchema)
    .output(ListSdTransactionIdsResponseSchema)
    .route({ method: "GET" }),
  markAsNoClaim: oc
    .input(MarkAsNoClaimInputSchema)
    .output(MarkAsNoClaimResponseSchema)
    .route({
      method: "POST",
    }),
  unmarkAsNoClaim: oc
    .input(UnmarkAsNoClaimInputSchema)
    .output(UnmarkAsNoClaimResponseSchema)
    .route({
      method: "POST",
    }),
  stage: oc
    .input(StageTransactionsInputSchema)
    .output(StageTransactionsResponseSchema)
    .route({
      method: "POST",
    }),
  unstage: oc
    .input(UnstageTransactionsInputSchema)
    .output(UnstageTransactionsResponseSchema)
    .route({
      method: "POST",
    }),
  unpost: oc
    .input(UnpostTransactionsInputSchema)
    .output(UnpostTransactionsResponseSchema)
    .route({
      method: "POST",
    }),
  updateStaged: oc
    .input(UpdateStagedTransactionInputSchema)
    .output(UpdateStagedTransactionResponseSchema)
    .route({
      method: "POST",
    }),
  bulkUpdate: oc
    .input(BulkUpdateTransactionsInputSchema)
    .output(BulkUpdateTransactionsResponseSchema)
    .route({
      method: "POST",
    }),
  addComment: oc
    .input(AddCommentInputSchema)
    .output(AddCommentResponseSchema)
    .route({
      method: "POST",
    }),
  editComment: oc
    .input(EditCommentInputSchema)
    .output(EditCommentResponseSchema)
    .route({
      method: "POST",
    }),
  deleteComment: oc
    .input(DeleteCommentInputSchema)
    .output(DeleteCommentResponseSchema)
    .route({
      method: "POST",
    }),
  stageInternal: oc
    .input(StageInternalTransactionsInputSchema)
    .output(StageInternalTransactionsResponseSchema)
    .route({
      method: "POST",
    }),
  unstageInternal: oc
    .input(UnstageInternalTransactionsInputSchema)
    .output(UnstageInternalTransactionsResponseSchema)
    .route({
      method: "POST",
    }),
  getStagedStats: oc
    .input(GetStagedStatsInputSchema)
    .output(GetStagedStatsResponseSchema)
    .route({
      method: "GET",
    }),
  getStagedSupplierBreakdown: oc
    .input(GetStagedSupplierBreakdownInputSchema)
    .output(GetStagedSupplierBreakdownResponseSchema)
    .route({
      method: "POST",
    }),
  getSelectedClaimAmount: oc
    .input(GetSelectedClaimAmountInputSchema)
    .output(GetSelectedClaimAmountResponseSchema)
    .route({
      method: "POST",
    }),
  getFilterOptions: oc
    .input(GetFilterOptionsInputSchema)
    .output(GetFilterOptionsResponseSchema)
    .route({
      method: "GET",
    }),
  downloadStagedExcel: oc
    .input(DownloadStagedExcelInputSchema)
    .output(DownloadStagedExcelResponseSchema)
    .route({
      method: "POST",
    }),
  downloadPendingExcel: oc
    .input(DownloadStagedExcelInputSchema)
    .output(DownloadStagedExcelResponseSchema)
    .route({
      method: "POST",
    }),
  downloadStagedInternalExcel: oc
    .input(DownloadStagedExcelInputSchema)
    .output(DownloadStagedExcelResponseSchema)
    .route({
      method: "POST",
    }),
  downloadNoClaimExcel: oc
    .input(DownloadStagedExcelInputSchema)
    .output(DownloadStagedExcelResponseSchema)
    .route({
      method: "POST",
    }),
  searchAll: oc
    .input(SearchAllTransactionsInputSchema)
    .output(SearchAllTransactionsResponseSchema)
    .route({
      method: "GET",
    }),
  getActivityLog: oc
    .input(GetActivityLogInputSchema)
    .output(GetActivityLogResponseSchema)
    .route({
      method: "GET",
    }),
  getClassificationStats: oc
    .input(GetClassificationStatsInputSchema)
    .output(GetClassificationStatsResponseSchema)
    .route({
      method: "GET",
    }),
  getStatusCounts: oc
    .input(GetStatusCountsInputSchema)
    .output(GetStatusCountsResponseSchema)
    .route({
      method: "GET",
    }),
  getClassificationBreakdown: oc
    .input(GetClassificationBreakdownInputSchema)
    .output(GetClassificationBreakdownResponseSchema)
    .route({
      method: "GET",
    }),
  getUserActivityStats: oc
    .input(GetUserActivityStatsInputSchema)
    .output(GetUserActivityStatsResponseSchema)
    .route({
      method: "GET",
    }),
  getSupplierAcceptanceRates: oc
    .input(GetSupplierAcceptanceRatesInputSchema)
    .output(GetSupplierAcceptanceRatesResponseSchema)
    .route({
      method: "GET",
    }),
  postNow: oc.input(PostNowInputSchema).output(PostNowResponseSchema).route({
    method: "POST",
  }),
  downloadPostingExcel: oc
    .input(DownloadPostingExcelInputSchema)
    .output(DownloadPostingExcelResponseSchema)
    .route({
      method: "POST",
    }),
} as const;

/**
 * Type exports
 */
export type ListSdTransactionsInput = z.infer<
  typeof ListSdTransactionsInputSchema
>;
export type ListSdTransactionsResponse = z.infer<
  typeof ListSdTransactionsResponseSchema
>;
export type ListSdTransactionIdsInput = z.infer<
  typeof ListSdTransactionIdsInputSchema
>;
export type ListSdTransactionIdsResponse = z.infer<
  typeof ListSdTransactionIdsResponseSchema
>;
export type MarkAsNoClaimInput = z.infer<typeof MarkAsNoClaimInputSchema>;
export type MarkAsNoClaimResponse = z.infer<typeof MarkAsNoClaimResponseSchema>;
export type UnmarkAsNoClaimInput = z.infer<typeof UnmarkAsNoClaimInputSchema>;
export type UnmarkAsNoClaimResponse = z.infer<
  typeof UnmarkAsNoClaimResponseSchema
>;
export type StageTransactionsInput = z.infer<
  typeof StageTransactionsInputSchema
>;
export type StageTransactionsResponse = z.infer<
  typeof StageTransactionsResponseSchema
>;
export type UnstageTransactionsInput = z.infer<
  typeof UnstageTransactionsInputSchema
>;
export type UnstageTransactionsResponse = z.infer<
  typeof UnstageTransactionsResponseSchema
>;
export type UnpostTransactionsInput = z.infer<
  typeof UnpostTransactionsInputSchema
>;
export type UnpostTransactionsResponse = z.infer<
  typeof UnpostTransactionsResponseSchema
>;
export type UpdateStagedTransactionInput = z.infer<
  typeof UpdateStagedTransactionInputSchema
>;
export type UpdateStagedTransactionResponse = z.infer<
  typeof UpdateStagedTransactionResponseSchema
>;
export type BulkUpdateTransactionsInput = z.infer<
  typeof BulkUpdateTransactionsInputSchema
>;
export type BulkUpdateTransactionsResponse = z.infer<
  typeof BulkUpdateTransactionsResponseSchema
>;
export type StageInternalTransactionsInput = z.infer<
  typeof StageInternalTransactionsInputSchema
>;
export type StageInternalTransactionsResponse = z.infer<
  typeof StageInternalTransactionsResponseSchema
>;
export type UnstageInternalTransactionsInput = z.infer<
  typeof UnstageInternalTransactionsInputSchema
>;
export type UnstageInternalTransactionsResponse = z.infer<
  typeof UnstageInternalTransactionsResponseSchema
>;
export type GetFilterOptionsInput = z.infer<typeof GetFilterOptionsInputSchema>;
export type GetFilterOptionsResponse = z.infer<
  typeof GetFilterOptionsResponseSchema
>;
export type DownloadStagedExcelInput = z.infer<
  typeof DownloadStagedExcelInputSchema
>;
export type DownloadStagedExcelResponse = z.infer<
  typeof DownloadStagedExcelResponseSchema
>;
export type GetActivityLogInput = z.infer<typeof GetActivityLogInputSchema>;
export type GetActivityLogResponse = z.infer<
  typeof GetActivityLogResponseSchema
>;
export type SearchAllTransactionsInput = z.infer<
  typeof SearchAllTransactionsInputSchema
>;
export type SearchAllTransactionsResponse = z.infer<
  typeof SearchAllTransactionsResponseSchema
>;
export type GetClassificationStatsInput = z.infer<
  typeof GetClassificationStatsInputSchema
>;
export type GetClassificationStatsResponse = z.infer<
  typeof GetClassificationStatsResponseSchema
>;
export type GetStatusCountsInput = z.infer<typeof GetStatusCountsInputSchema>;
export type GetStatusCountsResponse = z.infer<
  typeof GetStatusCountsResponseSchema
>;
export type GetSupplierAcceptanceRatesInput = z.infer<
  typeof GetSupplierAcceptanceRatesInputSchema
>;
export type GetSupplierAcceptanceRatesResponse = z.infer<
  typeof GetSupplierAcceptanceRatesResponseSchema
>;
export type PostNowInput = z.infer<typeof PostNowInputSchema>;
export type PostNowResponse = z.infer<typeof PostNowResponseSchema>;
export type DownloadPostingExcelInput = z.infer<
  typeof DownloadPostingExcelInputSchema
>;
export type DownloadPostingExcelResponse = z.infer<
  typeof DownloadPostingExcelResponseSchema
>;
